import Carousel from "@/components/slider/Carousel";
import StartupCard from "@/components/startup/new_card";
import useAuth from "@/hooks/auth";
import { useCountry } from "@/hooks/useCountry";
import { useSector } from "@/hooks/useSector";
import { useStage } from "@/hooks/useStage";
import { getWishlist, getWishlistCategory } from "@/services/api/Wishlist";
import { searchStartupPersonalized } from "@/services/api/Startup";
import { MagnifyingGlass } from "@phosphor-icons/react/dist/ssr";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import FilterMobile from "../drawer/filter-mobile";
import { Badge } from "../ui/badge";

function CarouselStartup({
  title,
  data,
  badges,
  onBadgeClick,
  activeBadge,
  totalAmount,
}) {
  const router = useRouter();

  const [openFindStartup, setOpenFindStartup] = useState(true);
  const [openFilterDetail, setOpenFilterDetail] = useState(false);
  const { countries } = useCountry({ searchStartupDropdown: true });
  const { data: sectors } = useSector({ searchStartupDropdown: true });
  const { data: stages } = useStage({ searchStartupDropdown: true });
  const { user } = useAuth();
  const [wishlistCategories, setWishlistCategories] = useState(null);
  const [userWishlist, setUserWishlist] = useState([]);
  const [userWishlistIds, setUserWishlistIds] = useState([]);

  // Filtered data state for badge filtering
  const [filteredData, setFilteredData] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);

  // Fetch filtered startups when badge is clicked
  useEffect(() => {
    if (!activeBadge) {
      // Reset to original data when "All Countries" is selected
      setFilteredData(null);
      return;
    }

    const fetchFilteredStartups = async () => {
      setIsFiltering(true);
      console.log("Filtering startups by badge:", activeBadge);
      try {
        const response = await searchStartupPersonalized({
          params: {
            per_page: 10,
            sort: "amount",
            countries: activeBadge.id,
          },
        });
        console.log("Filtered startups response:", response?.data);
        setFilteredData(response?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch filtered startups:", error);
        setFilteredData([]);
      } finally {
        setIsFiltering(false);
      }
    };

    fetchFilteredStartups();
  }, [activeBadge]);

  // Use filtered data if available, otherwise use original data
  const displayData = filteredData !== null ? filteredData : data;

  const getUserWishlist = async () => {
    const res = await getWishlist({
      per_page: 1000,
      type: 1,
    });
    setUserWishlist(res.data.data);
    setUserWishlistIds(res.data.data.map((v) => v.content_id));
  };

  const getWishlistCategories = async () => {
    if (wishlistCategories === null) {
      const res = await getWishlistCategory();
      setWishlistCategories(res.data);
    }
  };

  useEffect(() => {
    getUserWishlist();
    getWishlistCategories();
  }, []);

  const filterConfigs = useMemo(
    () => [
      {
        key: "countries",
        label: "Headquarter",
        type: "checkbox",
        data: countries.map((item) => ({
          id: item.id,
          name: item.country_name,
        })),
        queryKey: "countries",
      },
      {
        key: "sectors",
        label: "Sectors",
        type: "checkbox",
        data: sectors.map((item) => ({
          id: item.id,
          name: item.name,
        })),
        queryKey: "sectors",
      },
      {
        key: "stages",
        label: "Growth Stage",
        type: "checkbox",
        data: stages.map((item) => ({
          id: item.id,
          name: item.name,
        })),
        queryKey: "stages",
      },
      {
        key: "total_raised",
        label: "Fundraised Amount",
        type: "range",
        data: [],
        minQueryKey: "min_amount",
        maxQueryKey: "max_amount",
      },
      {
        key: "female_lead",
        label: "Female Lead",
        type: "radio",
        data: [
          { id: "all", name: "Show All", value: null },
          { id: "female", name: "Only Female-led startups", value: true },
        ],
        queryKey: "is_female_lead",
      },
      {
        key: "status",
        label: "Status",
        type: "radio",
        data: [
          { id: "active", name: "Active", value: null },
          { id: "inactive", name: "Inactive", value: true },
        ],
        queryKey: "is_inactive",
      },
      {
        key: "unicorn",
        label: "Unicorn",
        type: "radio",
        data: [
          { id: "all", name: "Show All", value: null },
          { id: "unicorn", name: "Unicorn", value: true },
          { id: "non-unicorn", name: "Non-unicorn", value: false },
        ],
        queryKey: "is_unicorn",
      },
      {
        key: "acquired",
        label: "Acquired",
        type: "radio",
        data: [
          { id: "all", name: "Show All", value: null },
          { id: "acquired", name: "Acquired", value: true },
          { id: "not-acquired", name: "Not Acquired", value: false },
        ],
        queryKey: "is_acquired",
      },
      {
        key: "ipo",
        label: "IPO",
        type: "radio",
        data: [
          { id: "all", name: "Show All", value: null },
          { id: "ipo", name: "IPO", value: true },
          { id: "no-ipo", name: "No IPO", value: false },
        ],
        queryKey: "is_ipo",
      },
    ],
    [countries, sectors, stages]
  );

  return (
    <div>
      {title && (
        <div className='container flex items-center justify-between gap-5 leading-none text-white lg:justify-normal lg:mb-5'>
          <span className='inline-flex items-center gap-2 lg:gap-2.5 text-xl font-medium leading-none text-white md:text-2xl lg:text-3xl font-display'>
            <span className='-translate-y-0.5 lg:-translate-y-1'>{title}</span>
            <span className='inline-flex items-center justify-center font-sans text-[10px] lg:text-xs font-semibold rounded-full py-1.5 px-2 leading-none bg-white/10'>
              {totalAmount}
            </span>
          </span>
          <span className='flex-1 hidden h-px lg:block bg-gradient-to-r from-yellow-1 to-red-1'></span>
          <FilterMobile
            filterConfigs={filterConfigs}
            initialQuery={router.query}
            redirectUrl='/startups'
            title='Find a Startup'
          >
            {({ openDrawer }) => (
              <button
                type='button'
                onClick={openDrawer}
                className='inline-flex items-center justify-center gap-1.5 px-3 py-2 lg:px-5 text-xs lg:text-sm font-semibold leading-none tracking-tight text-white rounded-full bg-gradient-to-tl from-red-1 to-orange-1'
              >
                <MagnifyingGlass weight='bold' />
                Find Startup
              </button>
            )}
          </FilterMobile>
        </div>
      )}

      {badges && badges.length > 0 && (
        <div className='container flex items-center gap-1 px-4 my-4 overflow-x-auto xl:px-20 no-scrollbar'>
          <Badge
            onClick={() => onBadgeClick(null)}
            className={`whitespace-nowrap cursor-pointer h-auto px-3 py-[5px] text-xs font-semibold rounded-full border transition-colors shadow-sm flex items-center ${!activeBadge
              ? "bg-white text-background border-white/5 hover:bg-white/80"
              : "bg-white/5 text-neutral-300 border-white/5 hover:bg-white/10"
              }`}
          >
            All Countries
          </Badge>
          {badges.map((badge) => (
            <Badge
              key={badge.id}
              onClick={() => onBadgeClick(badge)}
              className={`whitespace-nowrap cursor-pointer h-auto pl-1 pr-3 py-1 text-xs font-semibold rounded-full border transition-colors shadow-sm flex items-center ${activeBadge?.id === badge.id
                ? "bg-white text-background border-white/5 hover:bg-white/80"
                : "bg-white/5 text-neutral-300 border-white/5 hover:bg-white/10"
                }`}
            >
              <span
                className={`mr-1.5 px-1 font-semibold inline-flex items-center justify-center rounded-full text-[9px] ${activeBadge?.id === badge.id
                  ? "bg-black text-white"
                  : "bg-white/10 text-white"
                  }`}
              >
                {badge.count}
              </span>
              {badge.name}
            </Badge>
          ))}
        </div>
      )}

      {isFiltering ? (
        <div className="container flex items-center justify-center py-16">
          <div className="animate-pulse text-white/50">Loading...</div>
        </div>
      ) : (
        <Carousel>
          {displayData.map((item, index) => (
            <div
              key={index + "startup"}
              className='w-60 lg:w-64 h-full pt-[30px]'
            >
              <StartupCard
                startup={item}
                user={user}
                userWishlist={userWishlist}
                userWishlistIds={userWishlistIds}
                getUserWishlist={getUserWishlist}
                setWishlistCategories={setWishlistCategories}
                wishlistCategories={wishlistCategories}
                key={index}
                getWishlistCategories={getWishlistCategories}
              />
            </div>
          ))}
        </Carousel>
      )}
    </div>
  );
}
export default CarouselStartup;
